import { observer } from 'mobx-react';
import moment from 'moment/moment';
import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { formatVariableInput } from '../../../utils/variableInput';
import { store } from '../../../mobx/store';
import { useStores } from '../../../mobx/storeContext';
import { BASE_URL_NAME, MediaQueryMobileOnly } from '../../../utils/constants';
import { linesToDiv } from '../../../utils/utils';
import { EquationLines } from '../../EquationLines';
import { ResponsiveFlex, VFlex } from '../../Flex';
import DayKarKalLayout from '../CompanyLayout/DayKarKalLayout';
import MilwaysLayout from '../CompanyLayout/MilwaysLayout';

const EquationLinesWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  
  > div {
    width: 70%;
    min-width: 0;
  }
  
  ${MediaQueryMobileOnly} {
    * {
      white-space: normal !important;
      word-break: break-word;
      overflow-wrap: break-word;
    }
  }
`;

const BlackCell = styled.td`
background-color: #000;
color: #fff;
font-family: 'Noto Sans TC';
font-style: normal;
font-weight: 400;
font-size: 22px;
text-align: center;
align-items: center;
padding: 8px 6px;
white-space: normal;
word-wrap: break-word;
${MediaQueryMobileOnly} {
  font-size: 11px;
  padding: 4px 2px;
  line-height: 1.3;
}
`;

const WhiteCell = styled.td`
background-color: #fff;
color: #000;
font-family: 'Noto Sans TC';
font-style: normal;
font-weight: 400;
font-size: 22px;
letter-spacing: 0.2em;
text-align: center;
align-items: center;
padding: 6px 4px;
word-wrap: break-word;
overflow-wrap: break-word;
white-space: normal;
${MediaQueryMobileOnly} {
  font-size: 11px;
  padding: 4px 2px;
  letter-spacing: 0.02em;
  line-height: 1.3;
  min-width: 0;
}
`;

// Default to 'en' for i18n (project has no i18n)
const getLabel = (cell) => {
  if (!cell) return '';
  if (typeof cell === 'object' && cell.en != null) return String(cell.en).trim();
  return typeof cell === 'string' ? cell : '';
};

/**
 * 欄名：英文 → 繁 → 簡，**跳過空字串**。
 * 用 `??` 係捉唔到嘅 —— ERP 存落嚟嗰陣三個語言欄都會存在，冇填就係 `''`（唔係 undefined），
 * 而 `'' ?? x` 出返 `''`，所以淨係填咗中文嗰欄會出一個空白表頭，但下面啲格照樣有，
 * 睇落就好似「第 3 個 column 壞咗」。
 */
const getColumnLabel = col => (
  [col?.label, col?.labelHk, col?.labelCn].find(v => typeof v === 'string' && v.trim()) ?? ''
);

/** 呢一行係表格入面嘅間隔行？（ERP → Add Spacer Row） */
const getRowSpacer = (row) => {
  const t = row?.[0];
  if (!t || typeof t !== 'object' || !t.spacer) return null;
  return {
    height: Number.isFinite(Number(t.height)) ? Number(t.height) : 24,
    // 同 column 名一樣：跳過空字串，唔係淨係填咗中文嗰啲會出一片白。
    text: [t.en, t['zh-HK'], t['zh-CN']].find(v => typeof v === 'string' && v.trim()) ?? '',
  };
};

/** 呢一行係畀睇牌價嘅人自己填？（ERP → TV/牌價設定嗰個 Variable name） */
const getRowVariableName = (row) => {
  const t = row?.[0];
  if (!t || typeof t !== 'object') return '';
  return t.variableName || '';
};

const VariableInput = styled.input`
  /* 條底線唔好扯到成格咁闊 —— 同其他格啲數字（EquationLinesWrapper 都係 70%）睇齊。 */
  display: block;
  width: 70%;
  margin: 0 auto;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px solid #333;
  border-radius: 0;
  background: transparent;
  font-family: 'Noto Sans TC';
  font-size: 22px;
  text-align: center;
  padding: 4px 0;
  outline: none;
  &:focus { border-bottom-color: #d8b25a; }
  ${MediaQueryMobileOnly} {
    font-size: 13px;
    width: 85%;
  }
`;

/** 成格都撳得入去打字 —— 個 remark 佔咗格底一大截，撳落去唔 focus 就好似打唔到字。 */
const VariableCell = styled.label`
  display: block;
  width: 100%;
  cursor: text;
`;

/**
 * ERP 嗰格填嘅提示（例：「在此輸入印尼盾每KG價」）。以前係做 placeholder，一打字就冇咗，
 * 用戶睇唔返自己填緊乜 —— 所以獨立擺喺條線下面，長駐。
 */
const VariableRemark = styled.div`
  font-family: 'Noto Sans TC';
  font-size: 14px;
  letter-spacing: 0.05em;
  color: #777;
  text-align: center;
  padding-top: 4px;
  white-space: pre-line;
  ${MediaQueryMobileOnly} {
    font-size: 10px;
  }
`;

const CopyUrlButton = styled.button`
  font-family: 'Noto Sans TC';
  font-weight: 700;
  font-size: 28px;
  letter-spacing: 1.5px;
  color: #fff;
  background-color: #B9903E;
  border: none;
  border-radius: 20px;
  padding: 20px 64px;
  cursor: pointer;
  &:active {
    opacity: 0.8;
  }
  ${MediaQueryMobileOnly} {
    font-size: 21px;
    padding: 16px 48px;
  }
`;

/** Tab 名：英文 → 繁 → 簡，跳過空字串（同 column 名一樣）。 */
const getTabLabel = (tab, idx) => (
  [tab?.title, tab?.titleHk, tab?.titleCn].find(v => typeof v === 'string' && v.trim()) ?? `Tab ${idx + 1}`
);

const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin: 0 0 16px;
  ${MediaQueryMobileOnly} {
    gap: 6px;
    margin-bottom: 10px;
  }
`;

const TabButton = styled.button`
  font-family: 'Noto Sans TC';
  font-weight: 700;
  font-size: 20px;
  letter-spacing: 1px;
  padding: 10px 28px;
  border-radius: 20px;
  border: 2px solid #B9903E;
  background-color: ${p => (p.$active ? '#B9903E' : '#fff')};
  color: ${p => (p.$active ? '#fff' : '#B9903E')};
  cursor: pointer;
  &:active { opacity: 0.8; }
  ${MediaQueryMobileOnly} {
    font-size: 13px;
    padding: 6px 14px;
    border-radius: 14px;
  }
`;

const MissingVarHint = styled.div`
  font-family: 'Noto Sans TC';
  font-size: 15px;
  color: #d8b25a;
  text-align: center;
  padding: 0 12px;
  ${MediaQueryMobileOnly} {
    font-size: 12px;
  }
`;

type TTable = {
  columnTitles: { label: String, labelHk: String, labelCn: String }[],
  rows: Array<Array<{ en?: String } | String>>,
};

const Table = observer((props:TTable) => {
  const { columnTitles: allColumnTitles = [], rows = [], priceFetcherUpdateStore } = props;
  // 冇名嘅欄唔出：ERP 個編輯器一次過畀四格欄名，唔填嘅一樣會存落 config，出咗嚟就係一條
  // 空白表頭 + 一堆 `-`。記住原本嘅 index，因為每行嘅資料格係跟返 config 個次序。
  const visibleColumnIdx = allColumnTitles
    .map((col, idx) => (getColumnLabel(col) ? idx : -1))
    .filter(idx => idx >= 0);
  const columnTitles = visibleColumnIdx.map(idx => allColumnTitles[idx]);
  const numDataCols = columnTitles.length;
  const totalCols = numDataCols + 1; // +1 for row label column
  const numColWidth = totalCols > 0 ? `${100 / totalCols}%` : '20%';
  return (
    <table
      style={{
        width: store.isDesktop ? 'calc(100vw - 40px)' : 'calc(100vw - 10px)',
        maxWidth: '100%',
        fontFamily: 'Noto Sans TC',
        borderLeftWidth: 0,
        borderRightWidth: 0,
        borderTopWidth: 0,
        borderBottomWidth: 0,
        letterSpacing: store.isDesktop ? '0.2em' : '0.1em',
        borderCollapse: 'separate',
        borderSpacing: 0,
        tableLayout: 'auto',
        wordBreak: 'break-word',
      }}
    >
      <tr>
        <td style={{borderLeftWidth: 0, borderTopWidth: 0, borderBottomWidth: 0}} />
        {columnTitles.map((col, idx) => (
          <BlackCell
            key={getColumnLabel(col) || `col-${idx}`}
            style={{
              borderTopLeftRadius: idx === 0 ? 12 : 0,
              borderTopRightRadius: idx === numDataCols - 1 ? 12 : 0,
              borderLeftWidth: idx === 0 ? 2 : 0,
              borderRightWidth: idx === numDataCols - 1 ? 2 : 0,
              borderTopWidth: 2,
              width: numColWidth,
            }}
          >
            {getColumnLabel(col)}
          </BlackCell>
        ))}
      </tr>
      {
        rows.map((r, rowIdx) => {
          const labelCell = r[0];
          const label = getLabel(labelCell);
          const rowKey = `row-${rowIdx}-${label.substring(0, 10) || ''}`;
          const variableName = getRowVariableName(r);
          const spacer = getRowSpacer(r);
          // 間隔行：一格通到尾，淨係留返左右（同最尾嗰行嘅底）條邊，個框先唔會開口。
          if (spacer) {
            return (
              <tr key={rowKey}>
                <BlackCell
                  colSpan={totalCols}
                  style={{
                    height: spacer.height,
                    borderLeftWidth: 2,
                    borderRightWidth: 2,
                    borderBottomWidth: rowIdx === rows.length - 1 && 2,
                    borderBottomLeftRadius: rowIdx === rows.length - 1 && 12,
                    borderBottomRightRadius: rowIdx === rows.length - 1 && 12,
                    fontWeight: 600,
                    padding: 0,
                  }}
                >
                  {spacer.text}
                </BlackCell>
              </tr>
            );
          }
          return (
            <tr key={rowKey}>
              {
                (() => {
                  const arr = [];
                  for (let colIdx = 0; colIdx < totalCols; colIdx++) {
                    const style = {
                      borderTopLeftRadius: rowIdx === 0 && colIdx === 0 && 12,
                      borderLeftWidth: colIdx === 0 && 2,
                      borderRightWidth: colIdx === totalCols - 1 && 2,
                      borderBottomWidth: rowIdx === rows.length - 1 && 2,
                      borderBottomLeftRadius: rowIdx === rows.length - 1 && colIdx === 0 && 12,
                      borderBottomRightRadius: rowIdx === rows.length - 1 && colIdx === totalCols - 1 && 12,
                      width: numColWidth,
                    };
                    // `colIdx` 係「畫緊第幾格」，唔等於 config 裡面第幾欄 —— 冇名嗰啲欄
                    // 已經隔走咗，所以要 map 返去原本個 index 先攞得啱資料。
                    const sourceIdx = colIdx === 0 ? 0 : visibleColumnIdx[colIdx - 1] + 1;
                    const cellContent = colIdx === 0 ? label : String(r[sourceIdx] ?? '');
                    // 變數行：個輸入格由第一個資料 column 一路攤到最尾，成行都打得字。
                    // 所以呢類行唔應該再擺公式落去 —— 出唔到嚟。
                    if (variableName && colIdx === 1) {
                      arr.push(
                        <WhiteCell
                          style={{
                            ...style,
                            borderRightWidth: 2,
                            borderBottomRightRadius: rowIdx === rows.length - 1 ? 12 : undefined,
                            width: undefined,
                          }}
                          colSpan={totalCols - 1}
                          key={`${rowKey}-col-${colIdx}`}
                        >
                          <VariableCell>
                            <VariableInput
                              /* `type="text"`：`number` input 顯示唔到千分位。 */
                              type="text"
                              inputMode="decimal"
                              aria-label={label || variableName}
                              value={formatVariableInput(priceFetcherUpdateStore.draftVariables[variableName] ?? '')}
                              onChange={e => priceFetcherUpdateStore.setDraftVariable(
                                variableName,
                                formatVariableInput(e.target.value),
                              )}
                            />
                            {String(r[1] ?? '').trim() && (
                              <VariableRemark>{String(r[1]).trim()}</VariableRemark>
                            )}
                          </VariableCell>
                        </WhiteCell>,
                      );
                      break;
                    }
                    arr.push(
                      <WhiteCell
                        style={style}
                        key={`${rowKey}-col-${colIdx}`}
                      >
                        <EquationLinesWrapper>
                          <EquationLines
                            isShowTriangle
                            style={{ transition: 'all 0.3s' }}
                            lines={cellContent ? cellContent.split('\n') : []}
                          />
                        </EquationLinesWrapper>
                      </WhiteCell>,
                    );
                  }
                  return arr;
                })()
              }
            </tr>
          );
        })
      }
    </table>
  );
});

type TGoldConversionTable = {
  title: String,
  tableProps: TTable,
  lastUpdateTS: Number,
  bottomLeftText: any,
};

const GoldConversionTable = observer((props:TGoldConversionTable) => {
  const {title, tableProps, lastUpdateTS, bottomLeftText } = props;
  return (
    <VFlex style={{ alignItems: 'stretch', minWidth: 0, gap: 20 }}>
      {/* 表名跟返 webapp 個牌價板：金色、貼住張表左邊，唔再係一條散開嘅白字。 */}
      <h3
        style={{
          color: '#CDA659',
          letterSpacing: store.isDesktop ? 3 : 1.5,
          fontWeight: 700,
          marginTop: store.isDesktop ? 35 : 20,
          marginBottom: store.isDesktop ? -10 : -5,
          fontSize: store.isDesktop ? 22 : 15,
          // 張表本身係 `calc(100vw - 40px)` / `- 10px`，所以縮返一半就同表邊對齊。
          padding: store.isDesktop ? '0 20px' : '0 5px',
        }}
      >
        {title}
      </h3>
      <Table {...tableProps} />
      <ResponsiveFlex
        style={{
          justifyContent: 'space-between',
          alignSelf: 'stretch',
          padding: store.isDesktop ? 20 : 10,
          alignItems: 'flex-start',
          marginTop: store.isDesktop ? -10 : -5,
          gap: store.isDesktop ? 16 : 12,
          fontSize: store.isDesktop ? 18 : 12,
        }}
      >
        <VFlex>
          {linesToDiv(bottomLeftText)}
        </VFlex>
        <VFlex>
          {`最後更新時間：${moment(lastUpdateTS).format('YYYY-MM-DD HH:mm')} (GMT+8)`}
        </VFlex>
      </ResponsiveFlex>
    </VFlex>
  );
});

/** 個 project 有冇變數行？冇就唔使出「計算」掣。 */
const collectVariableNames = (project) => {
  const names = [];
  (project?.tables ?? []).forEach((t) => {
    (t.rows ?? []).forEach((r) => {
      const name = getRowVariableName(r);
      if (name && !names.includes(name)) names.push(name);
    });
  });
  return names;
};

const GoldPricePage = observer(() => {
  const {priceFetcherUpdateStore} = useStores();
  const [project, setProject] = useState();
  const Layout = BASE_URL_NAME === 'Milways' ? MilwaysLayout : DayKarKalLayout;
  const {pid} = useParams();
  const {search} = useLocation();
  const history = useHistory();
  const seededFromUrlRef = React.useRef(false);
  useEffect(() => {
    const p = priceFetcherUpdateStore?.data;
    setProject(p);
  }, [priceFetcherUpdateStore.refreshKey]);

  useEffect(() => {
    if (pid) {
      priceFetcherUpdateStore.setPid(pid);
    }
  }, [pid]);

  // 由 URL 帶返啲值入嚟，令後台個 log 嗰條連結一開就重現到當時嘅計算。
  // 只做一次：之後用戶自己改嗰啲唔應該畀 URL 蓋返轉頭。
  useEffect(() => {
    if (seededFromUrlRef.current) return;
    const names = collectVariableNames(project);
    if (!names.length) return;
    seededFromUrlRef.current = true;
    const params = new URLSearchParams(search);
    const seeded = {};
    names.forEach((name) => {
      const v = params.get(name);
      if (v != null && v.trim()) seeded[name] = v.trim();
    });
    if (!Object.keys(seeded).length) return;
    priceFetcherUpdateStore.setDraftVariables(seeded);
    priceFetcherUpdateStore.submitVariables();
  }, [project, search]);

  const variableNames = collectVariableNames(project);
  // 由 server 講返仲爭邊幾個未填（佢先知道啲 named equation 用咗邊個變數）。
  const missingVariables = project?.missingViewerVariables ?? [];

  const onCalculate = () => {
    const submitted = priceFetcherUpdateStore.submitVariables();
    // 將輸入寫返落 URL，等用戶 copy 條 link 出去（同後台個 log）都開得返同一個計算。
    const params = new URLSearchParams(search);
    variableNames.forEach((name) => {
      if (submitted[name] === undefined) params.delete(name);
      else params.set(name, String(submitted[name]));
    });
    const qs = params.toString();
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
  };

  // 打完即刻計，唔使撳掣。300ms debounce —— 唔係逐粒字都 send 一次 websocket。
  // `draftVariables` 每次 set 都係一個新 object，所以認得住識得 re-run。
  const {draftVariables} = priceFetcherUpdateStore;
  useEffect(() => {
    if (!Object.keys(draftVariables).length) return undefined;
    const timer = setTimeout(onCalculate, 300);
    return () => clearTimeout(timer);
  }, [draftVariables]);

  const [copied, setCopied] = useState(false);
  const onCopyUrl = async () => {
    try {
      await window.navigator.clipboard.writeText(window.location.href);
    } catch {
      // clipboard API 喺 http / 舊 browser 冇得用，行舊式 execCommand fallback。
      const ta = document.createElement('textarea');
      ta.value = window.location.href;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // 牌價板 tab（ERP → Board tab）：每個 tab 有自己條 link（`?tab=<id>`）。得返 0 / 1 個
  // 有效 tab 就照舊一版過出晒；唔屬於任何 tab 嘅表出喺揀咗嗰個 tab 下面。同 webapp 一樣。
  const tables = project?.tables ?? [];
  const tableIds = new Set(tables.map(t => t.id));
  const validTabs = (project?.tabs ?? []).filter(tab => (tab.tableIds ?? []).some(id => tableIds.has(id)));
  const showTabs = validTabs.length >= 2;
  const urlTab = new URLSearchParams(search).get('tab') ?? '';
  // URL 講嘅 tab 呢個 project 冇（改咗 id / copy 錯 link）就落返第一個。
  const activeTab = validTabs.find(tab => tab.id === urlTab) ?? validTabs[0];
  const selectTab = (id) => {
    const params = new URLSearchParams(search);
    params.set('tab', id);
    // 撳 tab 係去咗第二版，唔係 replace —— 部電話撳返上一頁要行得返轉頭。
    history.push({search: `?${params.toString()}`});
  };
  const tabbedIds = new Set(validTabs.flatMap(tab => tab.tableIds ?? []));
  const visibleTables = showTabs
    ? [...tables.filter(t => activeTab.tableIds.includes(t.id)), ...tables.filter(t => !tabbedIds.has(t.id))]
    : tables;

  return (
    <Layout>
      {showTabs && (
        <TabBar>
          {validTabs.map((tab, idx) => (
            <TabButton key={tab.id} type="button" $active={tab.id === activeTab.id} onClick={() => selectTab(tab.id)}>
              {getTabLabel(tab, idx)}
            </TabButton>
          ))}
        </TabBar>
      )}
      {
        project && visibleTables.map(t => (
          <GoldConversionTable
            key={t.id}
            title={t.title}
            tableProps={{
              columnTitles: t.columnTitles ?? [],
              rows: t.rows ?? [],
              priceFetcherUpdateStore,
            }}
            bottomLeftText={t.bottomLeftText}
            lastUpdateTS={Date.now()}
          />
        ))
      }
      {project && (
        <VFlex style={{alignItems: 'center', margin: '20px 0 0'}}>
          <CopyUrlButton type="button" onClick={onCopyUrl}>
            {copied ? '已複製 ✓' : 'COPY URL'}
          </CopyUrlButton>
        </VFlex>
      )}
      {variableNames.length > 0 && missingVariables.length > 0 && (
        <VFlex style={{alignItems: 'center', margin: '20px 0 40px', gap: 10}}>
          <MissingVarHint>
            {`請輸入 ${missingVariables.join('、')}，未填公式會出 0。`}
          </MissingVarHint>
        </VFlex>
      )}
    </Layout>
  );
});

export default GoldPricePage;
